'use client';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import DrawProof from './DrawProof';

const STEP = 300;

/**
 * The "Entrants list" tab: every paid or postal ticket in the competition
 * and who holds it (first name, surname initial, town), from the moment it
 * opens, updating as tickets are bought; once closed, the draw facts and
 * the verify button above it. Loaded when the tab is first opened.
 */
export default function EntrantsList({ competition }) {
  const id = competition?.id;
  const [data, setData] = useState(null); // /api/draws/<id>/entrants
  const [proof, setProof] = useState(null); // /api/draws/<id>
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(STEP);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    Promise.all([
      fetch(`/api/draws/${id}/entrants`).then((r) => (r.ok ? r.json() : null)),
      fetch(`/api/draws/${id}`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([entrants, draw]) => {
        if (cancelled) return;
        if (!entrants) setError('The entrants list could not be loaded.');
        setData(entrants);
        setProof(draw);
      })
      .catch(() => !cancelled && setError('The entrants list could not be loaded.'));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const entries = useMemo(() => data?.entries || [], [data]);
  const term = q.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      term
        ? entries.filter((e) => String(e.number) === term || e.name.toLowerCase().includes(term))
        : entries,
    [entries, term]
  );
  const shown = filtered.slice(0, limit);
  const winning = new Set((proof?.draw?.winning_numbers || []).map(Number));
  if (data?.winningTicket != null) winning.add(Number(data.winningTicket));

  if (error) {
    return <p className="text-muted-foreground">{error}</p>;
  }
  if (!data) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-soft bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {proof?.commitment && <DrawProof competitionId={id} proof={proof} />}

      {!data.published ? (
        <p className="text-muted-foreground">
          The entrants list is not available for this competition.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground tabular-nums">
                {entries.length.toLocaleString('en-GB')}
              </span>{' '}
              ticket{entries.length === 1 ? '' : 's'} entered
              {data.status === 'active' &&
                ' so far · entries are open and the list updates as tickets are bought'}
              {data.status === 'drawn' && winning.size > 0 && (
                <>
                  {' '}
                  · winning ticket{winning.size > 1 ? 's' : ''}{' '}
                  <span className="font-semibold text-accent-bright">
                    {[...winning].join(', ')}
                  </span>
                </>
              )}
            </p>
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Find a name or ticket number</span>
              <Icon
                name="MagnifyingGlassIcon"
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setLimit(STEP);
                }}
                placeholder="Find a name or ticket number"
                className="w-full rounded-soft border border-border bg-input py-2 pl-9 pr-3 text-sm text-foreground"
              />
            </label>
          </div>

          {filtered.length === 0 ? (
            <p className="text-muted-foreground">
              {entries.length === 0
                ? 'No entries yet. Be the first!'
                : 'No entries match that search.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-soft border border-border">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04] text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th scope="col" className="w-28 px-4 py-2.5 text-left">
                      Ticket
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left">
                      Entrant
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left">
                      Town
                    </th>
                    <th scope="col" className="w-28 px-4 py-2.5 text-left">
                      Entry
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {shown.map((e) => {
                    const won = winning.has(Number(e.number));
                    return (
                      <tr
                        key={e.number}
                        className={
                          won
                            ? 'bg-accent/15 font-semibold text-accent-bright'
                            : 'hover:bg-white/[0.03]'
                        }
                      >
                        <td className="px-4 py-2 font-mono tabular-nums">
                          #{String(e.number).padStart(String(data.totalTickets || 1).length, '0')}
                        </td>
                        <td className="px-4 py-2">
                          {e.name}
                          {won && (
                            <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-accent-foreground">
                              Winner
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{e.town || '—'}</td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {e.postal ? 'Postal' : 'Online'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {shown.length < filtered.length && (
            <button
              type="button"
              onClick={() => setLimit((n) => n + STEP)}
              className="link-more text-sm font-semibold"
            >
              Show more ({(filtered.length - shown.length).toLocaleString('en-GB')} left)
            </button>
          )}
        </>
      )}
    </div>
  );
}

EntrantsList.propTypes = {
  competition: PropTypes.shape({ id: PropTypes.string }).isRequired,
};
